<template>
    <widget
        :title="$tc('phrases.financeOverview',0, {period: periodText})"
        :id="'clusters-finance-overview'"
        button
        button-text="Set Period"
        button-color="red"
        @widgetAction="showPeriod"
        button-icon="calendar_today"
    >
        <div v-if="setPeriod" class="period-selector">
            <p>{{ $tc('phrases.selectPeriod') }}</p>
            <div class="md-layout md-gutter">
                <div class="md-layout-item md-size-100">
                    <md-datepicker v-model="period.from" md-immediately name="fromDate" v-validate="'required'" data-vv-name="fromDate">
                        <label>{{ $tc('phrases.fromDate') }}</label>
                    </md-datepicker>
                    <span class="md-error">{{ errors.first('fromDate') }}</span>
                </div>
                <div class="md-layout-item md-size-100">
                    <md-datepicker v-model="period.to" md-immediately name="toDate" v-validate="'required'" data-vv-name="toDate">
                        <label>{{ $tc('phrases.toDate') }}</label>
                    </md-datepicker>
                    <span class="md-error">{{ errors.first('toDate') }}</span>
                </div>
            </div>
            <div style="margin-top: 5px;">
                <button style="width:100%;" class="btn btn-primary" @click="getClusterFinancialData">
                    {{ $tc('words.send') }}
                </button>
            </div>
        </div>
        <div v-if="loading" class="charts-loader">
            <loader size="sm"/>
        </div>
        <div v-else class="md-layout md-gutter" style="padding: 10px">
            <chart-card type="LineChart"
                        :header-text="$tc('phrases.revenueLine')"
                        :chartData="lineChartData"
                        :chartOptions="chartOptions"
                        :extendable="true"
            />
            <chart-card type="ColumnChart"
                        :header-text="$tc('phrases.revenueColumns')"
                        :chartData="columnChartData"
                        :chartOptions="chartOptions"
                        :extendable="true"
            />
            <chart-card type="PieChart"
                        :header-text="$tc('phrases.revenuePercentiles')"
                        :chartData="pieChartData"
                        :chartOptions="chartOptions"
                        :extendable="true"
            />
        </div>

    </widget>


</template>

<script>
import Widget from '../../shared/widget'
import moment from 'moment'
import { ClusterService } from '@/services/ClusterService'
import ChartCard from '@/shared/ChartCard.vue'
import Loader from '@/shared/Loader.vue'
import { notify } from '@/mixins/notify'

function getDefaultPeriod () {
    return {
        from: moment().subtract(6, 'months').startOf('day').toDate(),
        to: moment().endOf('day').toDate(),
    }
}

function formatPeriodRange (from, to) {
    return `${moment(from).format('YYYY-MM-DD')} - ${moment(to).format('YYYY-MM-DD')}`
}

export default {
    name: 'FinancialOverview',
    components: { ChartCard, Loader, Widget },
    mixins: [notify],
    props: {
        clusterId: {
            type: Number | null,
            default: null,
        },
        revenue: {
            required: true,
        },
        periodChanged: {
            type: Function,
            default: null,
        },
        loading: {
            type: Boolean,
            default: false,
        },
    },
    data () {
        const defaultPeriod = getDefaultPeriod()
        return {
            clusterService: new ClusterService(),
            period: { ...defaultPeriod },
            setPeriod: false,
            periodText: formatPeriodRange(defaultPeriod.from, defaultPeriod.to),
            chartOptions: {
                chart: {
                    title: '',
                    subtitle: ''
                },
            },
        }
    },
    watch: {
        revenue: {
            immediate: true,
            handler () {
                this.applyPeriodFilter()
            }
        }
    },
    methods: {
        showPeriod () {
            if (!this.period.from || !this.period.to) {
                this.period = getDefaultPeriod()
            }
            this.setPeriod = !this.setPeriod
        },
        filterByPeriod (items, fromDate, toDate) {
            if (!Array.isArray(items)) {
                return []
            }

            const from = moment(fromDate, 'YYYY-MM-DD').startOf('month')
            const to = moment(toDate, 'YYYY-MM-DD').endOf('month')

            return items.map((item) => {
                const newPeriod = Object.entries(item.period || {}).reduce((acc, [period, revenue]) => {
                    const periodDate = moment(period, ['YYYY-MM', 'YYYY-MM-DD'], true)
                    if (
                        periodDate.isValid()
                        && periodDate.isSameOrAfter(from, 'month')
                        && periodDate.isSameOrBefore(to, 'month')
                    ) {
                        acc[period] = revenue
                    }
                    return acc
                }, {})

                return {
                    ...item,
                    period: newPeriod,
                    totalRevenue: Object.values(newPeriod).reduce(
                        (sum, entry) => sum + Number(entry?.revenue ?? 0),
                        0
                    ),
                }
            })
        },
        applyPeriodFilter () {
            const from = moment(this.period.from).format('YYYY-MM-DD')
            const to = moment(this.period.to).format('YYYY-MM-DD')
            this.clusterService.financialData = this.filterByPeriod(this.revenue ?? [], from, to)
            this.periodText = formatPeriodRange(this.period.from, this.period.to)
        },
        async getClusterFinancialData () {
            let validator = await this.$validator.validateAll()
            if (!validator) {
                return
            }

            this.applyPeriodFilter()
            this.setPeriod = false

            if (typeof this.periodChanged === 'function') {
                const from = moment(this.period.from).format('YYYY-MM-DD')
                const to = moment(this.period.to).format('YYYY-MM-DD')
                await this.periodChanged(from, to)
            }
        },
        getPeriodRange () {
            return {
                from: moment(this.period.from).format('YYYY-MM-DD'),
                to: moment(this.period.to).format('YYYY-MM-DD'),
            }
        },
    },
    computed: {
        lineChartData () {
            return this.clusterService.lineChartData(true)
        },
        columnChartData () {
            return this.clusterService.columnChartData(false, 'cluster')
        },
        pieChartData () {
            return this.clusterService.columnChartData(false, 'cluster')
        }
    }

}
</script>

<style>
.datepicker-right .vdp-datepicker__calendar {
    right: 0;
}


.period-selector {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 9999;
    padding: 15px;
    background-color: white;
    border: 1px solid #ccc;
    margin-right: 1rem;
    margin-top: 2rem
}

.charts-loader {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 240px;
    padding: 10px;
}


</style>
